import { ApprovalStep } from './expense.model';
import { PHIDataType } from './audit-log.model';

export enum QuestionStatus {
  draft = 'draft',
  open = 'open',
  investigating = 'investigating',
  answered = 'answered',
  deferred = 'deferred',
  invalidated = 'invalidated', // No longer relevant
}

export const QuestionStatusLabels: Record<QuestionStatus, string> = {
  [QuestionStatus.draft]: 'Draft',
  [QuestionStatus.open]: 'Open',
  [QuestionStatus.investigating]: 'Investigating',
  [QuestionStatus.answered]: 'Answered',
  [QuestionStatus.deferred]: 'Deferred',
  [QuestionStatus.invalidated]: 'Invalidated',
};

export enum QuestionPriority {
  low = 'low',
  medium = 'medium',
  high = 'high',
}

export const QuestionPriorityLabels: Record<QuestionPriority, string> = {
  [QuestionPriority.low]: 'Low',
  [QuestionPriority.medium]: 'Medium',
  [QuestionPriority.high]: 'High',
};

export interface QuestionAnswer {
  id: string;
  text: string;
  respondentId: string;
  respondentName: string;
  timestamp: Date;
  isAccepted: boolean;
}

export interface OpenQuestion {
  id: string;

  // Source
  meetingId: string;
  meetingTitle: string;
  sourceTimestampStart?: number;
  sourceTimestampEnd?: number;
  sourceTextSnippet?: string;

  // Question Content
  question: string;
  context?: string; // Background/context for the question
  category: string; // 'technical', 'business', 'process', etc.

  // Priority & Assignment
  priority: QuestionPriority;
  assignedTo?: string; // Who should answer
  assignedToId?: string;
  assignedAt?: Date;

  // Answer
  answer?: string;
  answeredBy?: string;
  answeredById?: string;
  answeredAt?: Date;
  answerSource?: string; // 'research', 'expert', 'documentation', etc.

  // Multiple Answers
  answers?: QuestionAnswer[];
  acceptedAnswerId?: string;

  // Status
  status: QuestionStatus;
  approvalWorkflow: ApprovalStep[];

  // Follow-up
  followUpRequired: boolean;
  followUpTaskId?: string;
  deferredUntil?: Date;
  deferralReason?: string;

  // Invalidation
  invalidatedReason?: string;
  invalidatedAt?: Date;
  invalidatedBy?: string;

  // PHI Detection
  containsPHI: boolean;
  phiDataTypes: PHIDataType[];

  // AI Confidence
  aiConfidence: number;
  aiExtractionNotes?: string;

  // Metadata
  tags: string[];
  relatedDecisionIds: string[];
  relatedTaskIds: string[];
  relatedRiskIds: string[];
  customFields?: Record<string, unknown>;

  // Audit
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
}

export interface QuestionAnalytics {
  totalQuestions: number;
  byStatus: Record<QuestionStatus, number>;
  byPriority: Record<QuestionPriority, number>;
  avgTimeToAnswer: number; // days
  answeredCount: number;
  deferredCount: number;
  openCount: number;
}
